/**
 * This directive is used to create a tooltip on the specified element.
 * Usage:
 *       import tooltip from '@/components/activix-ui/directives/tooltip';
 *       app.directive('tooltip', tooltip());
 *
 * // Then, in the template:
 * <span>
 *    This is a <span class="border-b border-dashed border-gray-400" v-tooltip="'Hello World'">tooltip</span>
 * </span>
 */

import tippy, { Instance as TippyInstance, Props as TippyProps } from 'tippy.js';
import { deviceType } from 'detect-it';
import { nextTick, type ObjectDirective } from 'vue';
import '@/components/activix-ui/directives/tippy.css';

export interface TooltipOptions extends Partial<TippyProps> {
    showOnMobile?: boolean;
    onlyOnTruncate?: boolean;
    showOnLoad?: boolean;
}

export interface TooltipHTMLElement extends HTMLElement {
    _tippy?: TippyInstance;
}

export type TooltipBindingValue = string | TooltipOptions;

export type DeviceType = 'mouseOnly' | 'touchOnly' | 'hybrid';

/**
 * Default tooltip options.
 */
export function getDefaultOptions(): TooltipOptions {
    return {
        arrow: false,
        animation: 'translate',
        offset: [0, 5],
        showOnMobile: false,
        onlyOnTruncate: false,
        onShow(instance: TippyInstance): false | void {
            if (!instance.props.content) {
                return false;
            }
            return undefined;
        },
    };
}

/**
 * Parses the binding value into tooltip options.
 */
export function parseBindingValue(
    bindingValue: TooltipBindingValue | undefined | null,
    defaults: TooltipOptions = getDefaultOptions()
): TooltipOptions {
    let options: TooltipOptions = { ...defaults };

    if (typeof bindingValue === 'object' && bindingValue !== null) {
        options = { ...options, ...bindingValue };
    } else if (typeof bindingValue === 'string') {
        options.content = bindingValue;
    }

    return options;
}

/**
 * Determines if tooltip should be shown based on device type.
 */
export function shouldShowOnDevice(
    showOnMobile: boolean,
    currentDeviceType: DeviceType = deviceType as DeviceType
): boolean {
    if (!showOnMobile && currentDeviceType === 'touchOnly') {
        return false;
    }
    return true;
}

/**
 * Determines if element content is truncated.
 */
export function isContentTruncated(el: HTMLElement): boolean {
    return el.offsetWidth < el.scrollWidth;
}

/**
 * Determines if tooltip should be initialized based on truncation setting.
 */
export function shouldInitialize(onlyOnTruncate: boolean, el: HTMLElement): boolean {
    if (onlyOnTruncate && !isContentTruncated(el)) {
        return false;
    }
    return true;
}

/**
 * Cleans up custom options before passing to tippy.
 */
export function cleanOptionsForTippy(options: TooltipOptions): Partial<TippyProps> {
    const cleaned = { ...options };
    delete cleaned.showOnMobile;
    delete cleaned.onlyOnTruncate;
    return cleaned as Partial<TippyProps>;
}

/**
 * Parses binding value for component updates (simpler parsing without defaults).
 */
export function parseBindingForUpdate(
    bindingValue: TooltipBindingValue | undefined | null
): Partial<TippyProps> {
    let options: Partial<TippyProps> = {};

    if (typeof bindingValue === 'object' && bindingValue !== null) {
        options = { ...bindingValue };
    } else if (typeof bindingValue === 'string') {
        options.content = bindingValue;
    }

    return options;
}

export default (): ObjectDirective => {
    return {
        async mounted(el: TooltipHTMLElement, binding) {
            await nextTick();

            const options = parseBindingValue(binding.value as TooltipBindingValue);

            if (!shouldShowOnDevice(options.showOnMobile ?? false)) {
                return;
            }

            if (!shouldInitialize(options.onlyOnTruncate ?? false, el)) {
                return;
            }

            const tippyOptions = cleanOptionsForTippy(options);
            tippy(el, tippyOptions);

            if (options.showOnLoad && el._tippy) {
                el._tippy.show();
            }
        },

        unmounted(el: TooltipHTMLElement) {
            if (!el._tippy) {
                return;
            }

            el._tippy.destroy();
        },

        updated(el: TooltipHTMLElement, binding) {
            if (!el._tippy) {
                return;
            }

            const options = parseBindingForUpdate(binding.value as TooltipBindingValue);
            el._tippy.setProps(options);
        },
    };
};
